'use client';

import * as React from 'react';
import { useQuery } from '@tanstack/react-query';

import { getInstrumentCandles, listInstruments } from '@/lib/api/admin-instruments';
import { useLiveQuotes } from '@/lib/streaming/use-live-quotes';
import { tickFlashClass, useTickFlash } from '@/lib/streaming/use-tick-flash';
import { formatPrice } from '@/lib/math/price';
import { cn } from '@/lib/util/cn';

/**
 * What the header ticker shows. Indices resolve by exact symbol in their
 * segment; futures resolve to the NEAREST live contract of the underlying, so
 * the strip rolls by itself. Edit this list to change the strip.
 */
const STRIP: { label: string; segment: string; symbol?: string; underlying?: string }[] = [
  { label: 'NIFTY 50', segment: 'NSE_IDX', symbol: 'NIFTY 50' },
  { label: 'BANK NIFTY', segment: 'NSE_IDX', symbol: 'NIFTY BANK' },
  { label: 'SENSEX', segment: 'BSE_IDX', symbol: 'SENSEX' },
  { label: 'INDIA VIX', segment: 'NSE_IDX', symbol: 'INDIA VIX' },
  { label: 'GOLD', segment: 'MCX_FO', underlying: 'GOLD' },
  { label: 'CRUDE', segment: 'MCX_FO', underlying: 'CRUDEOIL' },
];

type Resolved = { label: string; id: string; tick: number };

async function resolveStrip(signal?: AbortSignal): Promise<Resolved[]> {
  const out = await Promise.all(
    STRIP.map(async (s) => {
      const q = s.symbol ?? s.underlying ?? '';
      const r = await listInstruments(
        { q, segment_id: s.segment, ...(s.underlying ? { kind: 'future' as const } : {}), limit: 50 },
        signal,
      );
      const rows = r.data ?? [];
      const hit = s.symbol
        ? rows.find((i) => i.symbol === s.symbol)
        : rows
            .filter((i) => i.underlying_symbol === s.underlying && i.expiry)
            .sort((a, b) => Date.parse(a.expiry as string) - Date.parse(b.expiry as string))[0];
      return hit ? { label: s.label, id: hit.id, tick: hit.tick_size } : null;
    }),
  );
  return out.filter((x): x is Resolved => x !== null);
}

export function MarketStrip({ className }: { className?: string }): React.ReactElement {
  const stripQ = useQuery({
    queryKey: ['market-strip'],
    queryFn: ({ signal }) => resolveStrip(signal),
    staleTime: 60 * 60_000,
  });
  const items = React.useMemo(() => stripQ.data ?? [], [stripQ.data]);
  const ids = React.useMemo(() => items.map((i) => i.id), [items]);
  const { quotes } = useLiveQuotes(ids, 'touchline');

  return (
    <div className={cn('flex min-w-0 items-center gap-6 overflow-x-auto', className)}>
      {items.map((i) => (
        <Ticker key={i.id} id={i.id} label={i.label} quote={quotes[i.id] ?? null} tick={i.tick} />
      ))}
    </div>
  );
}

function Ticker({
  id,
  label,
  quote,
  tick,
}: {
  id: string;
  label: string;
  quote: { ltp?: number | null; close?: number | null; pct_change?: number | null } | null;
  tick: number | null;
}) {
  // Daily closes are the baseline: after hours the feed sends nothing for an
  // index, so the strip shows the last session's close and its change (as Kite
  // does). A live tick, when there is one, replaces the price.
  const dailyQ = useQuery({
    queryKey: ['strip-daily', id],
    queryFn: ({ signal }) => getInstrumentCandles(id, { timeframe: '1d', limit: 2, source: 'broker' }, signal),
    staleTime: 10 * 60_000,
  });
  const candles = dailyQ.data?.data.candles ?? [];
  const lastClose = candles.at(-1)?.close ?? null;
  const prevClose = candles.at(-2)?.close ?? null;
  const live = quote?.ltp ?? null;
  const ltp = live ?? quote?.close ?? lastClose;
  const flash = useTickFlash(live);
  const base = live != null ? (quote?.close ?? lastClose) : prevClose;
  const pct = ltp != null && base ? ((ltp - base) / base) * 100 : quote?.pct_change ?? null;
  return (
    <div className="flex shrink-0 items-baseline gap-2 whitespace-nowrap text-[13px]">
      <span className="text-fg-muted">{label}</span>
      <span key={flash.seq} className={cn('font-medium text-fg tabular-nums', tickFlashClass(flash))}>
        {ltp != null ? formatPrice(ltp, tick) : '—'}
      </span>
      {pct != null ? (
        <span className={cn('tabular-nums', pct > 0 ? 'text-long' : pct < 0 ? 'text-short' : 'text-fg-subtle')}>
          {pct > 0 ? '+' : ''}
          {pct.toFixed(2)}%
        </span>
      ) : null}
    </div>
  );
}
